import React, { useMemo, useState } from 'react';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import CenterModal from 'src/components/CenterModal';
import DateComponent from 'src/components/DateComponent';
import Icon from 'src/components/Icon';
import LoadingIcon from 'src/components/LoadingIcon';
import ModalHeader from 'src/components/ModalHeader';
import MfaEmailVerificationLink from 'src/components/MfaEmailVerificationLink';
import MfaSetupFlow from 'src/components/MfaSetupFlow';
import { MfaMethodType } from 'src/api/mfa';
import {
    useDeleteMfaMethod,
    useDeleteTrustedDevice,
    useMfaStatus,
    useTrustedDevices,
    useUpdateEmailMfaEnabled,
} from 'src/queries/useMfa';
import { parseError } from 'src/utils';
import { MFA_METHOD_LABELS, normalizeMfaMethodType } from 'src/utils/mfaMethods';
import styles from './styles.module.scss';

function MfaProfileSection() {
    const statusQuery = useMfaStatus();
    const trustedDevicesQuery = useTrustedDevices(Boolean(statusQuery.data));
    const deleteMethod = useDeleteMfaMethod();
    const deleteTrustedDevice = useDeleteTrustedDevice();
    const updateEmailMfaEnabled = useUpdateEmailMfaEnabled();
    const [isAdding, setIsAdding] = useState(false);
    const [removeError, setRemoveError] = useState('');
    const [emailError, setEmailError] = useState('');
    const status = statusQuery.data;
    const accountEmail = status?.user.accountEmail;

    const enabledMethods = useMemo(
        () =>
            (status?.user.methods || []).filter(
                (method) => {
                    const methodType = normalizeMfaMethodType(method.mfaMethodTyp);
                    return method.verifiedInd && method.actvInd !== false && methodType && methodType !== 'email';
                },
            ),
        [status],
    );
    const satisfyingMethods = useMemo(() => {
        if (!status) {
            return [];
        }

        return enabledMethods.filter((method) => {
            const methodType = normalizeMfaMethodType(method.mfaMethodTyp);
            return methodType && status.policy.allowedMethods.includes(methodType);
        });
    }, [enabledMethods, status]);
    const emailSatisfiesPolicy = Boolean(
        accountEmail?.verifiedInd &&
            accountEmail?.mfaEnabledInd &&
            status?.policy.allowedMethods.includes('email'),
    );
    const satisfyingMethodCount = satisfyingMethods.length + (emailSatisfiesPolicy ? 1 : 0);
    const addableMethods = useMemo(() => {
        if (!status) {
            return [];
        }

        return status.policy.allowedMethods.filter(
            (method, index, methods) =>
                method !== 'email' && MFA_METHOD_LABELS[method] && methods.indexOf(method) === index,
        );
    }, [status]);

    function isAcceptedByCurrentOrg(methodType: MfaMethodType) {
        return Boolean(status?.policy.allowedMethods.includes(methodType));
    }

    function isLastEnforcedSatisfyingMethod(methodType: MfaMethodType) {
        return (
            Boolean(status?.policy.enforced) &&
            !status?.user.exempt &&
            isAcceptedByCurrentOrg(methodType) &&
            satisfyingMethodCount === 1
        );
    }

    function isLastGraceSatisfyingMethod(methodType: MfaMethodType) {
        return (
            Boolean(status?.policy.inGracePeriod) &&
            !status?.user.exempt &&
            isAcceptedByCurrentOrg(methodType) &&
            satisfyingMethodCount === 1
        );
    }

    function isEmailToggleDisabled() {
        return Boolean(
            status?.policy.enforced &&
                !status?.user.exempt &&
                emailSatisfiesPolicy &&
                satisfyingMethodCount === 1,
        );
    }

    async function handleEmailMfaToggle(enabled: boolean) {
        try {
            setEmailError('');
            await updateEmailMfaEnabled.mutateAsync(enabled);
        } catch (e) {
            setEmailError(parseError(e));
        }
    }

    async function handleRemove(methodId: number, methodType: MfaMethodType) {
        if (isLastEnforcedSatisfyingMethod(methodType)) {
            setRemoveError('Add another MFA method accepted by this organization before removing this one.');
            return;
        }

        if (isLastGraceSatisfyingMethod(methodType)) {
            const confirmed = window.confirm(
                'Removing this method will leave your account without an MFA method accepted by this organization. You will be blocked after the grace period unless you add another accepted method.',
            );
            if (!confirmed) {
                return;
            }
        }

        try {
            setRemoveError('');
            await deleteMethod.mutateAsync(methodId);
        } catch (e) {
            setRemoveError(parseError(e));
        }
    }

    async function handleRevokeTrustedDevice(trustedDeviceId: number) {
        try {
            setRemoveError('');
            await deleteTrustedDevice.mutateAsync(trustedDeviceId);
        } catch (e) {
            setRemoveError(parseError(e));
        }
    }

    function renderTrustedDevices() {
        const trustedDevices = trustedDevicesQuery.data || [];
        if (trustedDevicesQuery.isLoading) {
            return (
                <div className={styles.trustedDeviceSection}>
                    <LoadingIcon />
                </div>
            );
        }

        if (trustedDevices.length === 0) {
            return null;
        }

        return (
            <div className={styles.trustedDeviceSection}>
                <div className={styles.sectionTitle}>Remembered browsers and devices</div>
                <div className={styles.trustedDeviceList}>
                    {trustedDevices.map((device) => {
                        const methodType = normalizeMfaMethodType(device.mfaMethodTyp) as MfaMethodType;
                        return (
                            <div className={styles.methodRow} key={device.usrMfaTrustedDeviceId}>
                                <div>
                                    <div className={styles.methodName}>
                                        {device.deviceLabel || (device.deviceTyp === 'mobile' ? 'Mobile device' : 'Browser')}
                                    </div>
                                    <div className={styles.methodDetail}>
                                        {device.deviceTyp === 'mobile' ? 'Mobile' : 'Web'} using{' '}
                                        {MFA_METHOD_LABELS[methodType] || device.mfaMethodTyp}
                                    </div>
                                    <div className={styles.methodDetail}>
                                        Expires{' '}
                                        <DateComponent date={device.expiresDttm} showDateTime={true} useBrowserTimeZone />
                                    </div>
                                </div>
                                <Button
                                    type="outline"
                                    text="Forget"
                                    onClick={() => handleRevokeTrustedDevice(device.usrMfaTrustedDeviceId)}
                                    isLoading={deleteTrustedDevice.isLoading}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderContent() {
        if (statusQuery.isLoading) {
            return <LoadingIcon />;
        }

        if (statusQuery.error || !status) {
            return <div className={styles.error}>Unable to load MFA methods.</div>;
        }

        return (
            <>
                <div className={styles.description}>
                    These MFA methods are tied to your login account and can be used for organizations that allow the
                    matching method.
                </div>

                {status.policy.required && !status.user.exempt && satisfyingMethodCount === 0 ? (
                    <div className={styles.warning}>
                        No enabled MFA method is accepted by this organization. Add an accepted method to satisfy this
                        organization&apos;s MFA policy.
                    </div>
                ) : null}

                <div className={styles.methodList}>
                    {accountEmail ? (
                        <div className={styles.methodRow}>
                            <div>
                                <div className={styles.methodName}>Account email</div>
                                <div className={styles.methodDetail}>{accountEmail.displayNm || accountEmail.emailAddr}</div>
                                <div className={accountEmail.verifiedInd ? styles.accepted : styles.notAccepted}>
                                    <span className={styles.statusLine}>
                                        <Icon
                                            className={accountEmail.verifiedInd ? styles.verifiedIcon : styles.unverifiedIcon}
                                            name={accountEmail.verifiedInd ? 'check-circle' : 'exclamation-circle'}
                                            type="fa"
                                        />
                                        {accountEmail.verifiedInd ? 'Verified email address' : 'Email address not verified'}
                                    </span>
                                </div>
                                {!accountEmail.verifiedInd ? (
                                    <div className={styles.notAccepted}>
                                        Verify this email address before using it for email MFA.
                                    </div>
                                ) : null}
                            </div>
                            <div className={styles.methodActions}>
                                {!accountEmail.verifiedInd ? (
                                    <MfaEmailVerificationLink
                                        destination={accountEmail.displayNm || accountEmail.emailAddr}
                                    />
                                ) : isAcceptedByCurrentOrg('email') ? (
                                    <label className={styles.emailMfaToggle}>
                                        <input
                                            type="checkbox"
                                            checked={accountEmail.mfaEnabledInd}
                                            disabled={isEmailToggleDisabled() || updateEmailMfaEnabled.isLoading}
                                            onChange={(event) => handleEmailMfaToggle(event.target.checked)}
                                        />
                                        Use for MFA
                                    </label>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {enabledMethods.length > 0 ? (
                        enabledMethods.map((method) => {
                            const methodType = normalizeMfaMethodType(method.mfaMethodTyp) as MfaMethodType;
                            const acceptedByOrg = isAcceptedByCurrentOrg(methodType);
                            const removeDisabled = isLastEnforcedSatisfyingMethod(methodType);
                            return (
                                <div className={styles.methodRow} key={method.usrMfaMethodId}>
                                    <div>
                                        <div className={styles.methodName}>{MFA_METHOD_LABELS[methodType]}</div>
                                        <div className={styles.methodDetail}>{method.displayNm || 'Verified'}</div>
                                        <div className={acceptedByOrg ? styles.accepted : styles.notAccepted}>
                                            {acceptedByOrg
                                                ? 'Accepted by this organization'
                                                : 'Not accepted by this organization'}
                                        </div>
                                    </div>
                                    <Button
                                        type="outline"
                                        text="Remove"
                                        title={
                                            removeDisabled
                                                ? 'Add another accepted MFA method before removing this one.'
                                                : undefined
                                        }
                                        disabled={removeDisabled}
                                        onClick={() => handleRemove(method.usrMfaMethodId, methodType)}
                                        isLoading={deleteMethod.isLoading}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className={styles.empty}>No additional MFA methods are enabled.</div>
                    )}
                </div>

                {removeError ? <div className={styles.error}>{removeError}</div> : null}
                {emailError ? <div className={styles.error}>{emailError}</div> : null}

                <div className={styles.actions}>
                    <Button
                        type="primary"
                        text="Add MFA method"
                        onClick={() => setIsAdding(true)}
                        disabled={addableMethods.length === 0}
                    />
                </div>

                {renderTrustedDevices()}
            </>
        );
    }

    return (
        <>
            <Card label="Multi-factor authentication">
                <div className={styles.content}>{renderContent()}</div>
            </Card>
            {isAdding && status ? (
                <CenterModal isOpen={isAdding} onClose={() => setIsAdding(false)} cardStyle={styles.addModalCard}>
                    <ModalHeader title="Add MFA method" onClose={() => setIsAdding(false)} />
                    <div className={styles.addModalBody}>
                        <MfaSetupFlow
                            status={status}
                            methodTypes={addableMethods}
                            purpose="enroll"
                            onVerified={() => setIsAdding(false)}
                        />
                        <div className={styles.addModalActions}>
                            <Button type="transparent" text="Cancel" onClick={() => setIsAdding(false)} />
                        </div>
                    </div>
                </CenterModal>
            ) : null}
        </>
    );
}

export default MfaProfileSection;
